import React, { useContext, useEffect, useState } from 'react';

import { LinearProgress } from '@material-ui/core';

import CaseInfoContent from './CaseInfoContent';
import FireDataContext from '../../../services/firebase/FireDataContext';
import MemberMiniMenu from '../menus/MemberMiniMenu';

// const neutralise = obj => JSON.parse(JSON.stringify(obj, (_, value) => value ?? ''));

import { timestamp } from '../../../app/data/validate';
import AuthContext from '../../../services/firebase/AuthContext';
import LoggingContext from '../../../services/logging/LoggingContext';

const caseInfoId = (courtCaseNumber, referrerId) => `${courtCaseNumber || '00000000'}-${referrerId.slice(0, 4)}`;

const CaseInfoPage = ({ uid }) => {
    const db = useContext(FireDataContext);
    const { currentUser } = useContext(AuthContext);
    const Errata = useContext(LoggingContext);

    const [user, setUser] = useState();
    const [cases, setCases] = useState();

    // default to current user if uid not provided
    uid = uid || currentUser.uid;

    useEffect(() => {

        if (currentUser.uid !== uid && !currentUser.roles.includes('admin')) {
            return;
        }

        (async () => {
            try {
                setUser(await db.get({ path: `users/${uid}` }));

                // // get the new single doc format cases first 
                const convertedCases = await db.get({
                    path: `cases`,
                    query: {
                        field: 'intermediaryId',
                        operator: '==',
                        value: uid
                    },
                    context: 'CaseInfoPage'
                });

                // get the intermediaries request for quotations that haven't been converted yet
                const rfqs = (await db.get({ path: `intermediaries/${uid}/referrals`, context: 'CaseInfoPage' }))
                    .filter(rfq => !convertedCases.find(item => item.id === caseInfoId(uid, rfq)));

                if (rfqs?.length) {
                    // get the referral data for each rfq 
                    const referrals = await Promise.all(
                        rfqs?.map(rfq => {
                            return db.get({ path: rfq.referralPath, context: 'CaseInfoPage' });
                        })
                    );

                    const referrers = await Promise.all(
                        referrals?.filter(Boolean).map(r => {
                            return db.get({ path: `users/${r.uid}`, context: 'CaseInfoPage' });
                        })
                    );

                    // get the court case info for each referral - anyone feeling nostalgic for RDBMS at this point :)
                    const courtCases = await Promise.all(
                        referrals?.filter(Boolean).map(({ organizationId, courtCaseId }) => {
                            return db.get({ path: `organizations/${organizationId}/cases/${courtCaseId}`, context: 'CaseInfoPage' })
                        })
                    );

                    const notes = await Promise.all(
                        referrals?.filter(Boolean).map(({ organizationId, id }) => {
                            return db.get({ path: `organizations/${organizationId}/notes/${id}`, context: 'CaseInfoPage' })
                        })
                    );

                    const quotes = await db.get({ path: `intermediaries/${uid}/quotes`, context: 'CaseInfoPage' });
                    const bookings = await db.get({ path: `intermediaries/${uid}/bookings`, context: 'CaseInfoPage' });
                    const timesheets = await db.get({ path: `intermediaries/${uid}/timesheets`, context: 'CaseInfoPage' });

                    // merge the individual docs into a set of related case info
                    const docs = rfqs.map(rfq => {
                        const referral = referrals.filter(Boolean).find(({ id }) => id === rfq.referralId);
                        const courtCase = courtCases.filter(Boolean).find(({ id }) => id === referral?.courtCaseId);
                        const quote = quotes.filter(Boolean).find(({ id }) => id === rfq.id);
                        const booking = bookings.filter(Boolean).find(({ referral: { id } }) => id === referral?.id);
                        const timesheet = timesheets.filter(Boolean).find(({ bookingId }) => bookingId === booking?.id);
                        const referrer = referrers.filter(Boolean).find(({ uid }) => uid === referral?.uid);
                        const note = notes.filter(Boolean).find(({ subjectId }) => subjectId === referral?.id);

                        const caseReference = courtCase?.courtCaseNumber || timesheet?.courtCaseNumber || '00000000';

                        return {
                            id: caseInfoId(caseReference, rfq.organizationId),
                            caseReference,
                            intermediaryId: uid,
                            // courtCase,
                            firstBookingReference: referral?.bookingReference,
                            info: {
                                courtCase: {
                                    _docPath: courtCase?._docPath,
                                    number: courtCase?.courtCaseNumber,
                                    court: quote?.court,
                                },
                                serviceUser: {
                                    _docPath: courtCase?._docPath,
                                    name: courtCase?.serviceUserName,
                                    age: referral?.client?.age,
                                    vulnerabilities: referral?.vulnerabilities || [],
                                },
                            },
                            referral: referral,
                            note: note,
                            work: [
                                {
                                    rfq,
                                    reference: referral?.bookingReference,
                                    quote: quote,
                                    booking: booking,
                                    timesheet: timesheet,
                                    tariff: referral?.tariff,
                                    location: referral?.location,
                                    courtCasePath: courtCase?._docPath
                                }
                            ],
                            referrer: {
                                user: { ...referrer },
                                organization: {
                                    id: rfq.organizationId,
                                    name: rfq.organization,
                                }
                            },
                        }
                    });

                    // merge the docs into cases based on the case number + referrer
                    const store = new Map();

                    for (const doc of docs.filter(({ caseReference }) => Boolean(caseReference))) {
                        const key = doc.id;

                        if (store.has(key)) {
                            const caseinfo = store.get(key);
                            store.set(key, { ...caseinfo, work: [...caseinfo.work, doc.work[0]] })
                        }
                        else {
                            store.set(key, doc);
                        }
                    }

                    // for (const doc of docs) {
                    //     try {
                    //         db.update({
                    //             path: `cases/${doc.id}`,
                    //             payload: neutralise(doc),
                    //             context: 'CaseInfoPage',
                    //         })
                    //     }
                    //     catch (error) {
                    //         console.log(error);
                    //     }
                    // }

                    setCases([...convertedCases, ...Array.from(store.values())]);

                }
                else {
                    setCases([...convertedCases]);
                }
            }
            catch (error) {
                Errata.log({ component: 'CaseInfoPage', user: currentUser, error });
            }
        })();
    }, [db, uid, currentUser.uid, currentUser.roles, currentUser, Errata]);

    const applyChange = ({ path, value, field }) => {
        db.update({
            path,
            payload: {
                [field]: value,
                ts: timestamp(),
                uid: currentUser.uid
            },
            context: 'CaseInfoPage'
        })
            .then(console.log('handleCaseInfoChange', path, field, value))
            .catch(error => Errata.log({ component: 'CaseInfoPage', user: currentUser, error }));
    }

    const updateInvoices = async ({ work, number = '', name = '' }) => {
        const courtCase = {
            courtCaseNumber: number,
            serviceUserName: name,
            uid,
            ts: timestamp()
        };

        // and it could be on the sales & purchase invoices ...
        await Promise.all(
            work.filter(({ booking }) => Boolean(booking?.completed?.purchaseInvoicePath))
                .map(({ booking: { completed: { purchaseInvoicePath } } }) => {
                    return db.update({
                        path: purchaseInvoicePath,
                        payload: courtCase,
                        context: 'CaseInfoPage'
                    })
                })
        );

        await Promise.all(
            work.filter(({ booking }) => Boolean(booking?.completed?.salesInvoicePath))
                .map(({ booking: { completed: { salesInvoicePath } } }) => {
                    return db.update({
                        path: salesInvoicePath,
                        payload: courtCase,
                        context: 'CaseInfoPage'
                    })
                })
        );
    }

    const handleCaseInfoChange = ({ id, key, value = '' }) => {

        const caseInfo = cases?.find(item => item.id === id) || {};

        try {

            const {
                info: {
                    courtCase: {
                        _docPath: courtCasePath,
                        courtCaseNumber = '',
                    },
                    serviceUser: {
                        _docPath: serviceUserPath,
                        name = ''
                    } = {}
                } = {},
                work = []
            } = caseInfo;

            // this is all a bit inelegant but it shouldn't be around for long once we're fully case based
            if (key === 'serviceUser.name' && serviceUserPath) {
                applyChange({ path: serviceUserPath, value, field: 'serviceUserName' });

                (async () => {
                    // it's also possible that there are multiple court case docs that need to be updated
                    Promise.all(
                        work.filter(({ courtCasePath }) => Boolean(courtCasePath))
                            .map(({ courtCasePath }) => {
                                return db.update({
                                    path: courtCasePath,
                                    payload: {
                                        serviceUserName: value
                                    },
                                    context: 'CaseInfoPage'
                                })
                            })
                    );

                    await updateInvoices({ work, number: courtCaseNumber, name: value });
                })();

            }

            if (key === 'serviceUser.age') {
                Promise.all(
                    work.map(({ rfq: { referralPath } }) => {
                        return db.update({
                            path: referralPath,
                            payload: {
                                client: {
                                    age: value
                                }
                            },
                            context: 'CaseInfoPage'
                        })
                    })
                );
            }

            if (key === 'courtCase.court') {
                Promise.all(
                    work.filter(({ quote }) => Boolean(quote))
                        .map(({ quote: { _docPath } }) => {
                            return db.update({
                                path: _docPath,
                                payload: {
                                    court: { ...value }
                                },
                                context: 'CaseInfoPage'
                            })
                        })
                );
            }

            if (key === 'courtCase.number' && courtCasePath) {
                applyChange({ path: courtCasePath, value, field: 'courtCaseNumber' });

                caseInfo.info.courtCase.number = value;
                caseInfo.caseReference = value;

                (async () => {
                    // quotes have a digest property that has a copy of the original court case number
                    // so update all the quotes in this case
                    await Promise.all(
                        work.filter(({ quote }) => Boolean(quote))
                            .map(({ quote: { _docPath, digest = {} } }) => {
                                return db.update({
                                    path: _docPath,
                                    payload: {
                                        digest: {
                                            ...digest,
                                            courtCaseNumber: value
                                        }
                                    },
                                    context: 'CaseInfoPage'
                                });
                            })
                    );

                    // it's also possible that there are multiple court case docs that need to be updated
                    await Promise.all(
                        work.filter(({ courtCasePath }) => Boolean(courtCasePath))
                            .map(({ courtCasePath }) => {
                                return db.update({
                                    path: courtCasePath,
                                    payload: {
                                        courtCaseNumber: value
                                    },
                                    context: 'CaseInfoPage'
                                })
                            })
                    );

                    await updateInvoices({ work, number: value, name });
                })();

            }
        }
        catch (error) {
            Errata.log({ component: 'CaseInfoPage', user: currentUser, error });
        }
        finally {
            // refresh the state 
            // setCases(cases.map(item => item.id === caseInfo.id ? caseInfo : item));
        }
    }

    return (
        <MemberMiniMenu>
            {
                cases && user ?
                    <CaseInfoContent member={user} cases={cases} onChange={handleCaseInfoChange} />
                    : <LinearProgress />
            }
        </MemberMiniMenu>
    );
};

export default CaseInfoPage;